import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import moment from 'moment';
import { parseDate } from '@datepicker-react/hooks';

import {
  DateType,
  formatMomentDate,
  FORM_DATE_FORMAT,
  getCurrentDate,
  isBeforeDateTime,
  URL_DATE_FORMAT,
  DEFAULT_DATE_FORMAT,
} from '@utils/dateUtil';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { SearchViewModel, WebUrlType } from '@api/v3';
import { HOTEL_PAST_SEARCH } from '@constants/constants';
import clean from '@utils/cleanObject';
import { callService } from '@api/remote-call';
import { handleFilterAndUpdateUrl } from '@utils/utils';
import { hotel_ga4Event } from '@utils/ga4Event/hotel_ga4Event';
import {
  getSafeLocalStorageItem,
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';
import { useAppRouter } from '@contexts/router';

import BookerGuestListingContainer from '../common/BookerGuestListingContainer';
import { BookerSearchViewModel } from './BookerSearchViewModel';
import HotelBookerDatePicker from '../HotelBookerDatePicker';
import {
  hotelItemsTOStringify,
  generateSearchRooms,
  validateGuestsChildrenAges,
} from '../common/bookerUtils';
import HotelSearchDrawer from './HotelSearchDrawer';
import { Wrapper } from './styled';

const placeholder = 'Yurt İçi - Yurt Dışı, Otel Adı veya Konum';

export interface HotelBookerError {
  location?: boolean;
}

interface HotelTabProps {
  onHrefUpdate: (href: string) => void;
  onSearchCalled: () => void;
  href: string;
  type: string;
  setError: Dispatch<SetStateAction<Partial<HotelBookerError | undefined>>>;
  setInitialError: Dispatch<
    SetStateAction<Partial<HotelBookerError | undefined>>
  >;
  setGuestError: Dispatch<SetStateAction<boolean>>;
  error?: HotelBookerError;
  initialError?: HotelBookerError;
  setLocalRooms?: Dispatch<SetStateAction<ListingSearchRoomInterface[]>>;
  localRooms: ListingSearchRoomInterface[];
}

const isDateOver = (date: Date) => {
  return isBeforeDateTime(
    moment(date),
    moment(
      parseDate(
        getCurrentDate().format(FORM_DATE_FORMAT),
        String(`dd/MM/yyyy`),
        new Date()
      )
    )
  );
};

export const HotelTabContent = ({
  onHrefUpdate,
  onSearchCalled,
  href,
  type,
  error,
  setError,
  setInitialError,
  initialError,
  setGuestError,
  setLocalRooms,
  localRooms,
}: HotelTabProps) => {
  const [hrefProps, setHrefProps] = useState<{
    [key: string]: string | number | string[] | undefined;
  }>();

  const [hotelRooms, setHotelRooms] = useState(localRooms);
  const [location, setLocation] = useState<BookerSearchViewModel | null>(null);
  const [localStartDate, setLocalStartDate] = useState<DateType | null>(null);
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(null);
  const [pastHotelItems, setPastHotelItems] = useState<any[]>([]);
  const [selectedPreviousHotel, setSelectedPreviousHotel] = useState(false);

  const router = useAppRouter();

  // ===================================================
  // Read past search items from local storage
  // Remove outdated items from the list
  // Update the local storage items
  // ===================================================
  useEffect(() => {
    const pastItems = getSafeLocalStorageItem(HOTEL_PAST_SEARCH)
      ? JSON.parse(getSafeLocalStorageItem(HOTEL_PAST_SEARCH) || '[]')
      : [];

    const filteredItem = pastItems.filter(
      (item: any) =>
        !isDateOver(parseDate(item.in, String(`yyyy-MM-dd`), new Date()))
    );

    setSafeLocalStorageItem(
      HOTEL_PAST_SEARCH,
      JSON.stringify(filteredItem, hotelItemsTOStringify)
    );

    setPastHotelItems(filteredItem);
  }, []);

  // ===========================================
  // Handel date change
  // ===========================================
  const onDatesChange = (dates?: {
    startDate?: DateType | null;
    endDate?: DateType | null;
  }) => {
    const props = {
      ...hrefProps,
      in: formatMomentDate(dates?.startDate, URL_DATE_FORMAT),
      out: formatMomentDate(dates?.endDate, URL_DATE_FORMAT),
      isBooker: 'true',
    };
    setHrefProps(props);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Handel on apply
  // ===========================================
  const onApplyGuest = (hr: ListingSearchRoomInterface[]) => {
    const roomUrlStr = generateSearchRooms(hr);
    const props = { ...hrefProps, room: roomUrlStr, isBooker: 'true' };

    setHrefProps(props);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  const onInputChange = (item: BookerSearchViewModel) => {
    const props = {
      ...hrefProps,
      slug: item?.url,
      isBooker: 'true',
    };

    setSelectedPreviousHotel(false);

    setHrefProps(props);
    setLocation(item);

    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Clear past search history items
  // ===========================================
  const clearPastHotelSearch = () => {
    removeSafeLocalStorageItem(HOTEL_PAST_SEARCH);
    setPastHotelItems([]);
  };

  // ===========================================
  // Update current rooms with past search rooms when clicking on past item
  // ===========================================
  const setPreviousHotelRooms = (item: BookerSearchViewModel) => {
    const targetItem = pastHotelItems.find(
      (i: BookerSearchViewModel) => i.text === item.text
    );

    setSelectedPreviousHotel(true);

    if (!targetItem) return;

    setHotelRooms(targetItem.rooms);
    setLocalRooms?.(targetItem.rooms);

    if (targetItem.in && targetItem.out) {
      setLocalStartDate(moment(targetItem.in));
      setLocalEndDate(moment(targetItem.out));
    }
    const roomUrlStr = generateSearchRooms(targetItem.rooms);

    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          slug: targetItem?.url,
          in: targetItem.in || null,
          out: targetItem.out || null,
          room: roomUrlStr,
        })
      )
    );
  };

  // ===========================================
  // Update the next search base on the popular search item
  // ===========================================
  const handlePopularItemSelected = (item: SearchViewModel) => {
    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          slug: item?.url,
        })
      )
    );
  };

  // ===========================================
  //Remove one item from local storage
  // ===========================================
  const removeItemFromLocalStorage = (item: BookerSearchViewModel) => {
    const newItems = pastHotelItems?.filter(
      (i: BookerSearchViewModel) => i.text !== item.text
    );

    setPastHotelItems(newItems);
    setLocalStartDate(null);
    setLocalEndDate(null);
    setSafeLocalStorageItem(
      HOTEL_PAST_SEARCH,
      JSON.stringify(newItems, hotelItemsTOStringify)
    );
  };
  // ===========================================
  // Keep the href props object up to date
  // ===========================================
  useEffect(() => {
    const roomUrlStr = generateSearchRooms(hotelRooms);
    setHrefProps({
      in: formatMomentDate(localStartDate, URL_DATE_FORMAT),
      out: formatMomentDate(localEndDate, URL_DATE_FORMAT),
      slug: location?.url,
      room: roomUrlStr,
      isBooker: 'true',
    });

    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          in: formatMomentDate(localStartDate, URL_DATE_FORMAT),
          out: formatMomentDate(localEndDate, URL_DATE_FORMAT),
          slug: location?.url,
          room: roomUrlStr,
          isBooker: 'true',
        })
      )
    );
  }, [localStartDate, localEndDate, location, hotelRooms]);

  // ===========================================
  // Handel save items to local storage
  // ===========================================
  const saveItems = async () => {
    if (!href || !type) return;

    let pastHotelSearches = [];
    let hotelItems: any[] = [];
    let newSearch: any = {};

    switch (type) {
      case WebUrlType.HOTEL_CITY:
      case WebUrlType.HOTEL_THEME:
      case WebUrlType.HOTEL_CHAIN:
      case WebUrlType.HOTEL_REGION:
      case WebUrlType.HOTEL:
        if (getSafeLocalStorageItem(HOTEL_PAST_SEARCH)) {
          pastHotelSearches = JSON.parse(
            getSafeLocalStorageItem(HOTEL_PAST_SEARCH) || '[]'
          );
        }

        hotelItems = pastHotelSearches ? [...pastHotelSearches] : [];

        if (location) {
          const data = await callService(
            {
              service: 'SearchesService',
              method: 'searchesHotel',
              version: 'v3',
            },
            {
              s: location.text as string,
            }
          );

          const targetLocation = data?.find(
            (item) => item.url === location.url
          );

          newSearch = {
            ...newSearch,
            ...targetLocation,
            slug: targetLocation?.url,
          };
        }

        if (localStartDate) {
          newSearch.in = formatMomentDate(localStartDate, URL_DATE_FORMAT);
        }
        if (localEndDate) {
          newSearch.out = formatMomentDate(localEndDate, URL_DATE_FORMAT);
        }

        newSearch.rooms = hotelRooms;

        if (!hotelItems.find((i) => i.url === location?.url)) {
          hotelItems.unshift(newSearch);
        } else {
          const target = hotelItems.find((i) => i.url === location?.url);

          const targetIndex = hotelItems.indexOf(target);

          hotelItems.splice(targetIndex, 1);
          hotelItems.unshift(newSearch);
        }

        setSafeLocalStorageItem(
          HOTEL_PAST_SEARCH,
          JSON.stringify(hotelItems, hotelItemsTOStringify)
        );
        router.push(href);
        break;

      default:
        router.push(href);
    }
  };

  const hasSavedItems = useRef(false);

  useEffect(() => {
    const isRoomsError = validateGuestsChildrenAges(hotelRooms);
    if (error?.location) return;
    if (isRoomsError) return setGuestError(true);
    setGuestError(false);
    if (!href || !type) return;
    if (hasSavedItems.current) return;
    saveItems();

    hasSavedItems.current = true;
    const hotelGa4EventData = {
      destination_point: location?.text || '',
      arrival_date: formatMomentDate(localEndDate, DEFAULT_DATE_FORMAT),
      departure_date: formatMomentDate(localStartDate, DEFAULT_DATE_FORMAT),
      room_number: hotelRooms.length.toString(),
      adult_number: hotelRooms
        .reduce((acc, room) => acc + room.adultCount, 0)
        .toString(),
      child_number: hotelRooms
        .reduce((acc, room) => acc + room.childrenList.length, 0)
        .toString(),
    };
    hotel_ga4Event(hotelGa4EventData);
  }, [onSearchCalled]);

  // ===========================================
  // Validate hotel location and hotel date
  // ===========================================
  useEffect(() => {
    if (!href.split('?')[0]) {
      setInitialError((val) => ({
        ...val,
        location: true,
      }));
    } else {
      setInitialError((val) => ({
        ...val,
        location: false,
      }));

      setError({
        ...initialError,
        location: false,
      });
    }
  }, [href, location]);

  useEffect(() => {
    if (hotelRooms?.length > 0) {
      setLocalRooms?.(hotelRooms);
    }
  }, [hotelRooms]);
  useEffect(() => {
    const now = moment();

    if (localStartDate && moment(localStartDate).isBefore(now, 'day')) {
      setLocalStartDate(null);
    }

    if (localEndDate && moment(localEndDate).isBefore(now, 'day')) {
      setLocalEndDate(null);
    }
  }, []);

  return (
    <Wrapper>
      <HotelSearchDrawer
        type='hotel'
        placeholder={placeholder}
        onInputLocationChange={onInputChange}
        responsiveTitle='Yurt İçi - Yurt Dışı, Otel Adı veya Konum'
        fetchItems={(param: string) => {
          return callService(
            {
              service: 'SearchesService',
              method: 'searchesHotel',
              version: 'v3',
            },
            {
              s: param || '',
            }
          );
        }}
        history={pastHotelItems?.slice(0, 3)}
        onClearClick={clearPastHotelSearch}
        callBack={setPreviousHotelRooms}
        onRemoveItem={removeItemFromLocalStorage}
        error={error?.location}
        onPopularSearchItemSelected={handlePopularItemSelected}
      />

      <HotelBookerDatePicker
        dateChange={onDatesChange}
        setLocalEndDate={setLocalEndDate}
        setLocalStartDate={setLocalStartDate}
        localStartDate={localStartDate}
        localEndDate={localEndDate}
        isPreviousSearch={selectedPreviousHotel}
      />

      <BookerGuestListingContainer
        hotelRooms={hotelRooms}
        setHotelRooms={setHotelRooms}
        onApply={onApplyGuest}
        isBookerSearch
      />
    </Wrapper>
  );
};
export default HotelTabContent;
